import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft, Upload, FileText, Sparkles, Loader2, AlertCircle,
  CheckCircle, X, Award, Building2, Calendar, Users, Shield,
  Clock, Star
} from "lucide-react";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { invokeFunction } from "../lib/invokeFunction";
import { extractTextFromPdf, isPdfFile } from "../lib/pdfExtractor";
import { supabase } from "../lib/supabase";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain"
];

function getFileTypeLabel(type) {
  if (type === "application/pdf") return "PDF";
  if (type.includes("word")) return "Word";
  if (type === "text/plain") return "TXT";
  return "File";
}

export default function BecomeExpertPage() {
  const { user } = useAuth();
  const [step, setStep] = useState("info");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError("Поддерживаются форматы: PDF, Word (.doc, .docx), TXT");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError("Максимальный размер файла: 5 МБ");
      return;
    }
    setError("");
    setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect({ target: { files: [dropped] } });
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      let fileContent;
      let isBase64 = false;

      if (isPdfFile(file)) {
        const pdfText = await extractTextFromPdf(file);
        if (pdfText.length < 50) {
          throw new Error("Не удалось извлечь текст из PDF. Попробуйте сохранить резюме в формате TXT.");
        }
        fileContent = pdfText;
      } else if (file.type === "text/plain") {
        fileContent = await file.text();
      } else {
        fileContent = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.readAsDataURL(file);
        });
        isBase64 = true;
      }

      const { data, error: fnError } = await invokeFunction("deepseek-parse-cv", {
        body: { fileContent, fileType: file.type, fileName: file.name, isBase64 }
      });
      if (fnError) throw new Error(fnError.message || "Ошибка анализа резюме");
      if (data?.profile) {
        setAiResult(data.profile);
        setStep("review");
      } else {
        throw new Error(data?.error || "AI не смог извлечь данные из файла");
      }
    } catch (err) {
      setError(err.message || "Ошибка при анализе резюме");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    setSubmitting(true);
    setError("");
    try {
      const applicationData = {
        user_id: user?.id || null,
        name: aiResult?.name || user?.name || "Без имени",
        email: user?.email || "",
        cv_file_name: file?.name || null,
        cv_file_type: file?.type || null,
        ai_summary: aiResult || null,
        status: "pending"
      };

      const { error: insertError } = await supabase
        .from("expert_applications")
        .insert(applicationData)
        .select();

      if (insertError) throw new Error(insertError.message);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Ошибка при отправке заявки");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg text-center"
          >
            <CheckCircle className="w-20 h-20 text-accent-400 mx-auto mb-6" />
            <h1 className="text-3xl font-medium text-white mb-4">
              Заявка отправлена!
            </h1>
            <p className="text-lg font-extralight text-neutral-300 mb-3">
              Спасибо за интерес к платформе «Обстучим». Мы внимательно изучим ваш опыт.
            </p>
            <p className="text-lg font-extralight text-neutral-400 mb-8">
              Отбор экспертов проводится раз в квартал. Мы свяжемся с вами по email, если ваша кандидатура подойдёт.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors"
              >
                На главную
              </Link>
              <Link
                to="/experts"
                className="px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-lg font-extralight transition-colors"
              >
                Посмотреть экспертов
              </Link>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div id="become-expert_page" className="min-h-screen">
        <section className="py-16 lg:py-24 bg-gradient-to-b from-primary-950/30 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">На главную</span>
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-2xl mx-auto mb-12"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-600/20 flex items-center justify-center">
                <Star className="w-10 h-10 text-primary-400" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-medium text-white mb-4">
                Стать экспертом
              </h1>
              <p className="text-xl font-extralight text-neutral-300 leading-relaxed">
                Делитесь своим опытом и помогайте другим решать бизнес-задачи
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-8 lg:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatePresence mode="wait">
              {step === "info" && (
                <motion.div
                  key="info"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-8"
                >
                  <div className="p-6 rounded-md bg-primary-500/5 border border-primary-500/20">
                    <div className="flex items-start gap-4">
                      <Shield className="w-6 h-6 text-primary-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-medium text-white mb-2">
                          Как проходит отбор
                        </h3>
                        <p className="text-lg font-extralight text-neutral-300 leading-relaxed">
                          Платформа «Обстучим» тщательно отбирает экспертов. Мы проводим набор <strong className="text-white font-medium">раз в 3 месяца</strong> и внимательно изучаем опыт каждого кандидата. Подача заявки не гарантирует получение статуса эксперта, но мы рассматриваем каждую анкету.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      {
                        icon: Upload,
                        title: "Загрузите CV",
                        description: "AI-помощник проанализирует ваш опыт и составит саммари"
                      },
                      {
                        icon: Users,
                        title: "Мы изучим заявку",
                        description: "Команда платформы внимательно рассмотрит вашу кандидатуру"
                      },
                      {
                        icon: Award,
                        title: "Получите ответ",
                        description: "Мы свяжемся с вами по email с результатом отбора"
                      }
                    ].map((item, i) => (
                      <div key={i} className="p-5 rounded-md bg-neutral-900/50 border border-neutral-800 text-center">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-md bg-primary-600/20 flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-primary-400" />
                        </div>
                        <h4 className="text-lg font-medium text-white mb-1">{item.title}</h4>
                        <p className="text-lg font-extralight text-neutral-400">{item.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                    <h3 className="text-xl font-medium text-white mb-4">
                      Загрузите ваше резюме
                    </h3>
                    <p className="text-lg font-extralight text-neutral-400 mb-6">
                      AI-помощник извлечёт ключевую информацию из вашего CV и составит профессиональное саммари для нашей команды.
                    </p>

                    {error && (
                      <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20 mb-4">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <p className="text-lg font-extralight text-red-300">{error}</p>
                      </div>
                    )}

                    {!user && (
                      <div className="flex items-center gap-3 p-4 rounded-md bg-amber-500/10 border border-amber-500/20 mb-4">
                        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <p className="text-lg font-extralight text-amber-300">
                          Для подачи заявки необходимо{" "}
                          <Link to="/register" className="text-primary-400 hover:text-primary-300 underline">
                            зарегистрироваться
                          </Link>{" "}
                          или{" "}
                          <Link to="/login" className="text-primary-400 hover:text-primary-300 underline">
                            войти
                          </Link>
                          .
                        </p>
                      </div>
                    )}

                    <div
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                        file
                          ? "border-primary-500/40 bg-primary-500/5"
                          : "border-neutral-700 hover:border-primary-500/30 hover:bg-neutral-800/30"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {file ? (
                        <div className="flex items-center justify-center gap-4">
                          <div className="w-12 h-12 rounded-md bg-primary-500/20 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-primary-400" />
                          </div>
                          <div className="text-left">
                            <p className="text-lg font-medium text-white">{file.name}</p>
                            <p className="text-lg font-extralight text-neutral-400">
                              {getFileTypeLabel(file.type)} / {(file.size / 1024).toFixed(0)} КБ
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                            className="p-2 rounded-md hover:bg-neutral-700 text-neutral-500 hover:text-white transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-neutral-500 mx-auto mb-3" />
                          <p className="text-lg font-extralight text-neutral-300 mb-1">
                            Перетащите файл сюда или нажмите для выбора
                          </p>
                          <p className="text-lg font-extralight text-neutral-500">
                            PDF, Word (.doc, .docx), TXT / до 5 МБ
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex justify-end mt-6">
                      <button
                        onClick={handleAnalyze}
                        disabled={!file || loading || !user}
                        className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
                      >
                        {loading ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Анализ резюме...</>
                        ) : (
                          <><Sparkles className="w-5 h-5" /> Проанализировать</>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === "review" && aiResult && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-start gap-3 p-4 rounded-md bg-accent-500/10 border border-accent-500/20">
                    <Sparkles className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-lg font-medium text-accent-300 mb-1">AI проанализировал ваше резюме</p>
                      <p className="text-lg font-extralight text-accent-300/80">
                        Ниже — саммари, которое увидит наша команда при рассмотрении заявки. Если всё верно — нажмите «Отправить заявку».
                      </p>
                    </div>
                  </div>

                  {aiResult.name && (
                    <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
                      <p className="text-lg font-extralight text-neutral-500 mb-1">Имя</p>
                      <p className="text-xl font-medium text-white">{aiResult.name}</p>
                    </div>
                  )}

                  {aiResult.title && (
                    <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
                      <p className="text-lg font-extralight text-neutral-500 mb-1">Должность</p>
                      <p className="text-lg font-medium text-white">{aiResult.title}</p>
                    </div>
                  )}

                  {aiResult.bio && (
                    <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
                      <p className="text-lg font-extralight text-neutral-500 mb-1">О себе</p>
                      <p className="text-lg font-extralight text-neutral-300 leading-relaxed whitespace-pre-line">
                        {aiResult.bio}
                      </p>
                    </div>
                  )}

                  {aiResult.companies?.length > 0 && (
                    <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-5 h-5 text-primary-400" />
                        <p className="text-lg font-extralight text-neutral-500">Места работы</p>
                      </div>
                      <div className="space-y-2">
                        {aiResult.companies.map((c, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 rounded bg-neutral-800/50">
                            <div className="flex-1">
                              <p className="text-lg font-medium text-white">{c.name}</p>
                              <p className="text-lg font-extralight text-neutral-400">{c.position}</p>
                            </div>
                            <div className="flex items-center gap-1.5 text-lg font-extralight text-neutral-500">
                              <Calendar className="w-4 h-4" />
                              {c.years}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiResult.achievements?.length > 0 && (
                    <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
                      <div className="flex items-center gap-2 mb-3">
                        <Award className="w-5 h-5 text-accent-400" />
                        <p className="text-lg font-extralight text-neutral-500">Достижения</p>
                      </div>
                      <ul className="space-y-2">
                        {aiResult.achievements.map((a, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-accent-400 flex-shrink-0 mt-1" />
                            <span className="text-lg font-extralight text-neutral-300">{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {error && (
                    <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-lg font-extralight text-red-300">{error}</p>
                    </div>
                  )}

                  <div className="flex justify-between gap-3 pt-4">
                    <button
                      onClick={() => { setStep("info"); setAiResult(null); setFile(null); }}
                      className="flex items-center gap-2 px-5 py-3 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> Загрузить другой файл
                    </button>
                    <button
                      onClick={handleSubmitApplication}
                      disabled={submitting}
                      className="flex items-center gap-2 px-8 py-3 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
                    >
                      {submitting ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Отправка...</>
                      ) : (
                        <><CheckCircle className="w-5 h-5" /> Отправить заявку</>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </div>
    </Layout>
  );
}
